import { defineStore } from 'pinia';
import CreateMailoutApi from '@/api';
import type {
  MyOrder,
  DraftPayload,
  DraftData,
  OrderDraft,
  ExcelDraftData,
  PdfDraftData,
  DeliveryDraftData,
  ContactDraftData,
} from '@/types/myOrders';
import constant from '@/constant';

const DRAFT_STORAGE_KEY = 'orderDraft';
const { MY_ORDER_SECTION } = constant;

const readDraftFromStorage = (): OrderDraft | null => {
  try {
    const raw = localStorage.getItem(DRAFT_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as OrderDraft;
  } catch {
    return null;
  }
};

const writeDraftToStorage = (data: OrderDraft): void => {
  try {
    localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Ignore storage quota errors
  }
};

const createEmptyDraft = (): OrderDraft => ({
  excel: null,
  pdf: null,
  delivery: null,
  contact: null,
  lastUpdated: '',
  payment: '',
});

export const useMyOrdersStore = defineStore('myOrders', {
  state: () => ({
    orders: [] as MyOrder[],
    error: null as string | null,
    draft: readDraftFromStorage() ?? createEmptyDraft(),
  }),
  actions: {
    rehydrateDraftFromStorage(): void {
      this.draft = readDraftFromStorage() ?? createEmptyDraft();
    },

    async fetchMyOrders(): Promise<void> {
      this.error = null;
      try {
        const response = await CreateMailoutApi.getMyOrders();
        this.orders = response;
      } catch (err) {
        this.error = err instanceof Error ? err.message : 'Failed to load orders';
        throw err;
      }
    },

    _persistDraft(): void {
      writeDraftToStorage(this.draft);
    },

    saveDraft(type: DraftPayload['type'], data: DraftData): void {
      switch (type) {
        case MY_ORDER_SECTION.EXCEL:
          this.draft.excel = data as ExcelDraftData;
          break;
        case MY_ORDER_SECTION.PDF:
          this.draft.pdf = data as PdfDraftData;
          break;
        case MY_ORDER_SECTION.DELIVERY:
          this.draft.delivery = data as DeliveryDraftData;
          break;
        case MY_ORDER_SECTION.CONTACT:
          this.draft.contact = data as ContactDraftData;
          break;
        case MY_ORDER_SECTION.PAYMENT:
          this.draft.payment = data as string;
          break;
      }
      this.draft.lastUpdated = new Date().toISOString();
      this._persistDraft();
    },

    clearExcelDraft(): void {
      this.draft.excel = null;
      this.draft.lastUpdated = new Date().toISOString();
      this._persistDraft();
    },

    clearPdfDraft(): void {
      this.draft.pdf = null;
      this.draft.lastUpdated = new Date().toISOString();
      this._persistDraft();
    },

    clearDraft(): void {
      this.draft = createEmptyDraft();
      localStorage.removeItem(DRAFT_STORAGE_KEY);
      localStorage.removeItem('selectedDate');
      localStorage.removeItem('priceData');
    },
  },
});
